'use client';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import { t } from '../../i18n';

import Button from '../Button/Button';
import CountryList from '../CountryList/CountryList';
import HeadLine from '../HeadLine/HeadLine';
import Flag from '../Flag/Flag';
import Grid from '../Grid/Grid';
import Box from '../Box/Box';
import SingleDataValueItem from '../SingleDataValueItem';
import boundingBoxes from '../../utils/country-bounding-boxes.json';
import Icon from '../Icon';
import ImageTextList from '../ImageTextList/ImageTextList';
import AnimatedLogo from '../AnimatedLogo/AnimatedLogo';
import MapFilter from '../MapFilter/MapFilter';
import TwoColumnDataValue from '../TwoColumnDataValue/TwoColumnDataValue';
import classNames from 'classnames';
import iconType from '../../utils/icontype';
import {
    formatValue,
    formatNumberWithSuffix,
    formatNumberWithSuffixAsString,
} from '../../utils/helpers';
import AccordionItem from '../../components/AccordionItem/AccordionItem';
import TabArea from '../TabArea/TabArea';
import { createMapConfig } from '../../utils/map/config';
import { getCountryBounds, padCountryBounds } from '../../utils/map/bounds';
import {
    addCountryBoundariesLayer,
    addPointsSource,
    addClusterLayers,
    addUnclusteredPointLayers,
    addWorldBoundsDebugLayer,
    setHoldingsLayersVisibility,
} from '../../utils/map/layers';
import { setupMapEventHandlers } from '../../utils/map/events';
import { MapProvider, useMap } from './MapContext';
import { useBookmarkContext } from '../../utils/bookmark-provider';
import { useSidebar } from '../../utils/sidebar-provider';
import { getHoldingsChunk } from '../../api/holdings';

import s from './Map.module.scss';
import { usePathname, useSearchParams } from 'next/navigation';
import Bookmark from '../Bookmark/Bookmark';

const DEBUG_BOUNDS = false;
const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;
const WORLD_BOUNDS_PADDING = 20;

// Shared so the default never changes identity between renders: the memo feeding
// setCountryList is keyed on it, and a fresh literal per render loops the provider.
const EMPTY_COUNTRY_LIST = { countries: [] };

const getMinMaxBoundsFromCountryList = (countryList) => {
    const countryListBounds = countryList.countries
        .map((country) => {
            const countryCode = country.code.toUpperCase();
            const countryBoundingBox = boundingBoxes[countryCode];

            if (countryBoundingBox) {
                const countryBounds = countryBoundingBox[1];
                return countryBounds;
                // return padCountryBounds(countryBounds, WORLD_BOUNDS_PADDING);
            }
            return null;
        })
        .filter(Boolean);

    if (countryListBounds.length === 0) {
        return null;
    }

    const minLng = Math.min(...countryListBounds.map((bounds) => bounds[0]));
    const minLat = Math.min(...countryListBounds.map((bounds) => bounds[1]));
    const maxLng = Math.max(...countryListBounds.map((bounds) => bounds[2]));
    const maxLat = Math.max(...countryListBounds.map((bounds) => bounds[3]));

    return [minLng, minLat, maxLng, maxLat];
};

const MapComponent = ({
    pois: bePois = { features: [] },
    countryList = EMPTY_COUNTRY_LIST,
    content = {},
    isSingleCountry = false,
    // The popup a clicked holdings point raises on the map itself. The world
    // map has always shown it; a single-country map that wants it opts in.
    isPointPopupShown = !isSingleCountry,
    isHoldingsShown = false,
    // Separate from isHoldingsShown: the world map plots holdings without the
    // holdings chrome. Only a map that shows no holdings at all opts out.
    // The ask latches — asking after mount starts the fetch, and withdrawing
    // it later neither cancels the request nor discards what arrived.
    shouldFetchHoldings = true,
    // Visibility of the holdings markers, independent of fetching them:
    // hiding keeps the data, so showing again needs no new request.
    isHoldingsLayerShown = true,
    // The full-map "Loading holdings…" curtain. Only a caller whose whole map
    // is holdings wants it; one drawing holdings beside other layers opts out.
    isLoadingOverlayShown = true,
    // A caller plotting its own points frames the map itself. The country box
    // is then neither the right extent nor a bound worth locking the view to,
    // since what happened to a country need not have happened inside it.
    shouldFrameSingleCountry = true,
    isFiltersShown = false,
    filtersLayout,
    overviewAccordion,
    singleCountryCode,
    isToolboxPage = false,
    // Lifts the wrapper's tablet height cap, which is sized for a map with a
    // list beside it. For a map that is a page's whole content and takes its
    // height from its container at every width.
    isFullHeight = false,
    // Squares the wrapper's bottom corners, for a caller that closes the map
    // with a strip of its own flush against it: two rounded edges meeting
    // leave a notch at each end.
    isBottomFlush = false,
    onMapReady,
    layers = [],
    activeCountryName,
    totalPoisCount = 0,
    initialPoisCount = 0,
    ssrCountryCode = null,
    // The page path the backend resolves the client's dashboard from. A caller
    // that knows its page's path passes it; otherwise the browser's is used,
    // which is wrong inside the CMS preview, where the browser is on /_draft.
    htmlPath = null,
}) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const popupRef = useRef(null);
    const { setMap, setCountryList } = useMap();
    const { bookmarkedPages } = useBookmarkContext();
    const { isSidebarShown } = useSidebar();
    const [allHoldingsLoaded, setAllHoldingsLoaded] = useState(false);
    const [isListView, setIsListView] = useState(true);

    const worldBounds = getMinMaxBoundsFromCountryList(countryList);

    const [pois, setPois] = useState(bePois);
    const [isFetchingChunks, setIsFetchingChunks] = useState(false);

    // Once asked, stays asked: a later false neither cancels nor discards,
    // and asking again never duplicates features already fetched.
    const [holdingsRequested, setHoldingsRequested] =
        useState(shouldFetchHoldings);
    useEffect(() => {
        if (shouldFetchHoldings) {
            setHoldingsRequested(true);
        }
    }, [shouldFetchHoldings]);

    useEffect(() => {
        if (!holdingsRequested) {
            setAllHoldingsLoaded(true);
            return;
        }

        setAllHoldingsLoaded(false);
        let isCancelled = false;
        setIsFetchingChunks(true);

        const fetchChunks = async () => {
            let currentPage = 1;
            const chunkSize = 500;
            // Track SSR'd country to deduplicate (uppercase for comparison)
            const ssrCountryUpper = ssrCountryCode?.toUpperCase();

            while (!isCancelled) {
                try {
                    const chunkData = await getHoldingsChunk(
                        currentPage,
                        chunkSize,
                        singleCountryCode,
                        htmlPath || window.location.pathname
                    );

                    if (isCancelled) break;

                    const newFeatures =
                        ssrCountryUpper && !singleCountryCode
                            ? chunkData.features.filter(
                                  (f) =>
                                      f.properties.countryCode?.toUpperCase() !==
                                      ssrCountryUpper
                              )
                            : chunkData.features;
                    setPois((prev) => ({
                        type: 'FeatureCollection',
                        features: [...prev.features, ...newFeatures],
                    }));

                    if (!chunkData.hasMore) {
                        setAllHoldingsLoaded(true);
                        break;
                    }

                    currentPage += 1;
                    await new Promise((resolve) => setTimeout(resolve, 100));
                } catch (error) {
                    console.error('Error fetching holdings chunk:', error);
                    break;
                }
            }

            setIsFetchingChunks(false);
        };

        fetchChunks();

        return () => {
            isCancelled = true;
        };
    }, [singleCountryCode, ssrCountryCode, holdingsRequested, htmlPath]);

    const mapPois = useMemo(
        () => ({
            ...pois,
            features: pois.features.filter(
                (f) => f.properties.category === 'Holding'
            ),
        }),
        [pois]
    );

    const computedCountryHoldingStats = useMemo(() => {
        const activeUpper = (
            singleCountryCode ||
            ssrCountryCode ||
            ''
        ).toUpperCase();
        if (!activeUpper) return content.countryHoldingStats || {};

        const byClientMap = {};
        let totalSites = 0;

        for (const feature of pois.features) {
            const props = feature.properties || {};
            if (props.category !== 'Holding') continue;
            if (props.countryCode?.toUpperCase() !== activeUpper) continue;

            totalSites += 1;
            const name = props.name || '';
            if (!byClientMap[name]) {
                byClientMap[name] = { revenue: 0, employees: 0 };
            }
            byClientMap[name].revenue += props.revenue || 0;
            byClientMap[name].employees += props.employeeCount || 0;
        }

        return {
            totalSites,
            byClient: Object.entries(byClientMap).map(([name, m]) => ({
                name,
                ...m,
            })),
            byType: content.countryHoldingStats?.byType || {},
            sitesByType: content.countryHoldingStats?.sitesByType || {},
        };
    }, [
        pois.features,
        singleCountryCode,
        ssrCountryCode,
        content.countryHoldingStats,
    ]);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [lng, setLng] = useState(0);
    const [lat, setLat] = useState(50);
    const [zoom, setZoom] = useState(1);
    const [activeCountryCode, setActiveCountryCode] = useState(
        singleCountryCode || ssrCountryCode
    );
    const activeCountryCodeRef = useRef(activeCountryCode);
    useEffect(() => {
        activeCountryCodeRef.current = activeCountryCode;
    }, [activeCountryCode]);
    const [activeHolding, setActiveHolding] = useState(null);
    const [activeCluster, setActiveCluster] = useState(null);

    // Read by the map's async load handler, which outlives the render that
    // registered it, so the layers are added with the current visibility
    const isHoldingsLayerShownRef = useRef(isHoldingsLayerShown);

    useEffect(() => {
        if (isSingleCountry) {
            setActiveCountryCode(singleCountryCode);
        }
    }, [isSingleCountry, singleCountryCode]);

    // Separate effect for handling URL parameter activation
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const idParam = urlParams.get('id');

        if (!idParam || activeHolding === idParam) {
            return;
        }

        // Check if holding is in current pois
        const holdingExists = pois.features.some(
            (f) => f.properties.sourceId === idParam
        );

        if (holdingExists) {
            setActiveCluster(null);
            setActiveHolding(idParam);
        }
    }, [pois.features, activeHolding]);

    const bounds = getCountryBounds(singleCountryCode);

    const countryCodes = useMemo(
        () =>
            countryList.countries.map((country) => country.code.toUpperCase()),
        [countryList.countries]
    );

    let countryFilterCodes = [...countryCodes];

    if (
        countryFilterCodes.includes('US') &&
        !countryFilterCodes.includes('PR')
    ) {
        countryFilterCodes.push('PR');
    }

    const initializeMap = () => {
        mapboxgl.accessToken = MAPBOX_TOKEN;
        mapboxgl.setTelemetryEnabled?.(false);
        if (map.current) return;

        const mapConfig = createMapConfig(
            mapContainer.current,
            isSingleCountry,
            lng,
            lat,
            zoom
        );

        map.current = new mapboxgl.Map(mapConfig);
        setMap(map.current);
        onMapReady?.(map.current);

        if (!isSingleCountry) {
            setTimeout(() => {
                // If SSR'd with a country, zoom to that country
                if (ssrCountryCode) {
                    const countryBBox =
                        boundingBoxes[ssrCountryCode.toUpperCase()]?.[1];
                    if (countryBBox) {
                        map.current.fitBounds(padCountryBounds(countryBBox), {
                            duration: 0,
                        });
                        return;
                    }
                }
                if (worldBounds) {
                    map.current.fitBounds(worldBounds, {
                        padding: {
                            top: WORLD_BOUNDS_PADDING,
                            bottom: WORLD_BOUNDS_PADDING,
                            left: WORLD_BOUNDS_PADDING,
                            right: WORLD_BOUNDS_PADDING,
                        },
                        duration: 0,
                    });
                }
            }, 400);
        } else if (bounds && shouldFrameSingleCountry) {
            setTimeout(() => {
                map.current.fitBounds(padCountryBounds(bounds), {
                    duration: 0,
                });
                map.current.setMaxBounds(map.current.getBounds());
            }, 400);
        }

        map.current.addControl(
            new mapboxgl.NavigationControl({ showCompass: false }),
            'bottom-right'
        );

        map.current.on('load', () => {
            setMap(map.current);
            onMapReady?.(map.current);
            addCountryBoundariesLayer(map.current, countryFilterCodes);

            if (!isSingleCountry && worldBounds && DEBUG_BOUNDS) {
                addWorldBoundsDebugLayer(map.current, worldBounds);
            }

            addPointsSource(map.current, mapPois);
            addClusterLayers(map.current, isHoldingsLayerShownRef.current);
            addUnclusteredPointLayers(
                map.current,
                activeHolding,
                isHoldingsLayerShownRef.current
            );

            setupMapEventHandlers(map.current, {
                setLng,
                setLat,
                setZoom,
                setActiveCountryCode,
                setActiveHolding,
                setActiveCluster,
                handleCountryClick,
                isSingleCountry,
                isPointPopupShown,
                countryList,
                popupRef,
            });
        });

        if (isToolboxPage) {
            map.current.on('click', (e) => {
                const clusters = map.current.queryRenderedFeatures(e.point, {
                    layers: ['clusters'],
                });
                const points = map.current.queryRenderedFeatures(e.point, {
                    layers: ['unclustered-point', 'unclustered-point-circle'],
                });

                if (clusters.length || points.length) return;

                if (popupRef.current) {
                    popupRef.current.remove();
                    popupRef.current = null;
                }
                setActiveCluster(null);
                setActiveHolding(null);

                const params = new URLSearchParams(window.location.search);
                params.delete('id');
                window.history.pushState(
                    {},
                    '',
                    `${window.location.pathname}?${params.toString()}`
                );
            });
        }
    };

    const zoomToWorld = () => {
        if (map?.current && worldBounds) {
            map.current.fitBounds(worldBounds, {
                padding: {
                    top: WORLD_BOUNDS_PADDING,
                    bottom: WORLD_BOUNDS_PADDING,
                    left: WORLD_BOUNDS_PADDING,
                    right: WORLD_BOUNDS_PADDING,
                },
                duration: 1000,
            });

            setTimeout(() => {
                map?.current?.resize?.();
                map?.current?.triggerRepaint?.();
            }, 150);
            return;
        }

        setTimeout(() => {
            initializeMap();
            map?.current?.resize?.();
            map?.current?.triggerRepaint?.();
        }, 150);
    };

    // initializeMap early-returns if map.current exists, so only countryCodes matters for re-init
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        initializeMap();
    }, [countryCodes]);

    useEffect(() => {
        isHoldingsLayerShownRef.current = isHoldingsLayerShown;
        if (!map.current) return;
        setHoldingsLayersVisibility(map.current, isHoldingsLayerShown);
    }, [isHoldingsLayerShown]);

    useEffect(() => {
        if (activeHolding || activeCluster) {
            const holding = pois.features.find(
                (feature) => feature.properties.sourceId === activeHolding
            );

            if (holding && map.current) {
                const currentZoom = map.current.getZoom();
                const targetZoom = 6;
                const zoomLevel = Math.max(currentZoom, 16);

                if (currentZoom < targetZoom) {
                    map.current.flyTo({
                        center: holding.geometry.coordinates,
                        zoom: zoomLevel,
                        duration: 4000,
                    });
                } else {
                    map.current.flyTo({
                        center: holding.geometry.coordinates,
                        zoom: currentZoom,
                        duration: 1000,
                    });
                }
            }
        }

        // Scrolls the holdings panel below the map into view
        if (isHoldingsShown) {
            window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [activeHolding, activeCluster, pois.features, isHoldingsShown]);

    const handleCountryClick = (country) => {
        if (!country) {
            setActiveCountryCode(null);
            setIsDropdownOpen(false);
            return;
        }

        setActiveCountryCode(country.code);
        setIsDropdownOpen(false);
        if (!activeCountryCodeRef.current) {
            setTimeout(() => {
                const countryBBox =
                    boundingBoxes[country.code.toUpperCase()][1];
                if (countryBBox) {
                    map.current.fitBounds(padCountryBounds(countryBBox), {
                        duration: 1000,
                    });
                }
                map.current.resize();
            }, 150);
        } else {
            const countryBBox = boundingBoxes[country.code.toUpperCase()][1];
            if (countryBBox) {
                map.current.fitBounds(padCountryBounds(countryBBox), {
                    duration: 1000,
                });
            }
            map.current.resize();
        }
    };

    const countryListProps = useMemo(
        () => ({
            ...countryList,
            isDropdownOpen,
            setIsDropdownOpen,
            activeCountryCode,
            onCountryClick: handleCountryClick,
        }),
        [countryList, isDropdownOpen, activeCountryCode]
    );

    useEffect(() => {
        setCountryList(countryListProps);
    }, [countryListProps, setCountryList]);

    const getCountryName = (code) => {
        const country = countryList.countries.find(
            (country) => country.code.toLowerCase() === code
        );
        return country ? country.name : '';
    };

    useEffect(() => {
        if (!map.current) return;
        map.current.resize();
        map.current.once('idle', () => {
            map.current?.resize();
        });
    }, [activeCountryCode]);

    useEffect(() => {
        if (
            isHoldingsShown &&
            activeHolding === null &&
            activeCluster === null
        ) {
            map.current.flyTo({
                center: [0, 50],
                zoom: 1,
                duration: 1000,
            });
        }
    }, [isHoldingsShown, activeHolding, activeCluster]);

    const handleShowWorldMap = () => {
        if (popupRef.current) {
            popupRef.current.remove();
            popupRef.current = null;
        }
        setActiveHolding(null);
        const params = new URLSearchParams(window.location.search);
        params.delete('id');
        params.delete('activeCountry');
        sessionStorage.removeItem('activeCountry');

        const newUrl = `${window.location.pathname}?${params.toString()}`;

        window.history.pushState({}, '', newUrl);

        zoomToWorld();
        setTimeout(() => {
            setActiveCountryCode(null);
        }, 200);
    };

    const handleCloseHoldingDetails = () => {
        setActiveCluster(null);
        setActiveHolding(null);
        const params = new URLSearchParams(window.location.search);
        params.delete('id');
        const newUrl = `${window.location.pathname}?${params.toString()}`;

        window.history.pushState({}, '', newUrl);

        map.current.flyTo({
            center: [0, 50],
            zoom: 1,
            duration: 1000,
        });
        setTimeout(() => {
            map.current.resize();
        }, 300);
    };

    const classes = classNames(s.Root, {
        [s['Root--ShowDetails']]: !isSingleCountry && activeCountryCode,
        [s['Root--Holdings']]: isHoldingsShown,
        [s['Root--Toolbox']]: isToolboxPage,
        [s['Root--FullHeight']]: isFullHeight,
        [s['Root--BottomFlush']]: isBottomFlush,
        [s['Root--ListView']]: isHoldingsShown && isListView,
    });

    useEffect(() => {
        if (!map.current || !map.current.getLayer('unclustered-point-circle'))
            return;

        if (activeHolding) {
            map.current.setPaintProperty(
                'unclustered-point-circle',
                'circle-color',
                [
                    'case',
                    ['==', ['get', 'sourceId'], activeHolding],
                    '#D2E8FF',
                    '#ffffff',
                ]
            );

            map.current.setPaintProperty(
                'unclustered-point-circle',
                'circle-stroke-color',
                [
                    'case',
                    ['==', ['get', 'sourceId'], activeHolding],
                    '#3D96FF',
                    '#ACD5FF',
                ]
            );
        } else {
            map.current.setPaintProperty(
                'unclustered-point-circle',
                'circle-color',
                [
                    'case',
                    ['==', ['get', 'sourceId'], activeHolding],
                    '#ffffff',
                    '#ffffff',
                ]
            );
            map.current.setPaintProperty(
                'unclustered-point-circle',
                'circle-stroke-color',
                [
                    'case',
                    ['==', ['get', 'sourceId'], activeHolding],
                    '#ACD5FF',
                    '#ACD5FF',
                ]
            );
        }
    }, [activeHolding]);

    useEffect(() => {
        if (!map.current) return;
        map.current.resize();
        map.current.once('idle', () => {
            map.current?.resize();
        });
    }, [isFiltersShown]);

    useEffect(() => {
        if (!map.current) return;
        map.current.resize();
        map.current.once('idle', () => {
            map.current?.resize();
            map.current?.triggerRepaint();
        });
    }, [isSidebarShown]);

    const waitForSource = (id) =>
        new Promise((resolve, reject) => {
            let attempts = 0;
            const MAX_ATTEMPTS = 300;
            const check = () => {
                if (map.current?.getSource(id)) resolve();
                else if (++attempts > MAX_ATTEMPTS)
                    reject(new Error(`waitForSource('${id}') timed out`));
                else requestAnimationFrame(check);
            };
            check();
        });

    useEffect(() => {
        if (!map.current) return;

        const run = async () => {
            await waitForSource('points');

            const updated = {
                ...mapPois,
                features: mapPois.features.map((f) => ({
                    ...f,
                    properties: {
                        ...f.properties,
                        isActive: f.properties.sourceId === activeHolding,
                    },
                })),
            };

            map.current.getSource('points').setData(updated);
        };

        // On a map that never shows holdings the points source never arrives,
        // so the wait times out — with nothing drawn there is nothing to
        // restyle, not an error to surface.
        run().catch(() => {});
    }, [activeHolding, mapPois]);

    return (
        <div className={classes}>
            {!isSingleCountry && (
                <>
                    {/* <div className={s.CountryList}>
                        <CountryList
                            {...countryList}
                            isOpen={isDropdownOpen}
                            onToggle={setIsDropdownOpen}
                            onCountryClick={handleCountryClick}
                            activeCountryCode={activeCountryCode}
                        />
                    </div> */}
                    {/* <Button
                        className={s.ShowWorldMapButton}
                        title="Back to world map"
                        modifier={isToolboxPage ? 'Secondary' : 'SecondaryGrey'}
                        onClick={handleShowWorldMap}
                        disabled={
                            activeCountryCode === null || zoom < 2 || zoom == 1
                        } // zoom == 1 is for mobile initial zoom
                    /> */}
                </>
            )}

            {isHoldingsShown && (
                <div className={s.MobileOnly}>
                    <MapFilter
                        holdings={pois.features.map(
                            (feature) => feature.properties
                        )}
                        locationTypes={content.locationTypes}
                        activeHoldingId={activeHolding}
                        setActiveHolding={(id) => {
                            setActiveCluster(null);
                            setActiveHolding(id);
                        }}
                        country={activeCountryCode}
                        allHoldingsLoaded={allHoldingsLoaded}
                        isLoading={isFetchingChunks && !allHoldingsLoaded}
                        isListView={isListView}
                        onToggleView={() => setIsListView((prev) => !prev)}
                    />
                </div>
            )}

            <div className={s.MapWrapper}>
                {layers.length > 0 && (
                    <div className={s.Layers}>
                        {layers.map((layer, index) =>
                            React.cloneElement(layer, {
                                key: index,
                                zoomToWorld,
                            })
                        )}
                    </div>
                )}
                <div className={s.Map} ref={mapContainer} />
                {isLoadingOverlayShown &&
                    isFetchingChunks &&
                    !allHoldingsLoaded && (
                        <div className={s.MapLoadingOverlay}>
                            <div className={s.LoadingContent}>
                                <AnimatedLogo
                                    width={29}
                                    height={34}
                                    stroke="#616161"
                                />
                                <div className={s.LoadingText}>
                                    Loading holdings…
                                </div>
                            </div>
                        </div>
                    )}
                {isHoldingsShown && (
                    <div className={s.DesktopOnly}>
                        <MapFilter
                            holdings={pois.features.map(
                                (feature) => feature.properties
                            )}
                            locationTypes={content.locationTypes}
                            activeHoldingId={activeHolding}
                            setActiveHolding={(id) => {
                                setActiveCluster(null);
                                setActiveHolding(id);
                            }}
                            country={activeCountryCode}
                            allHoldingsLoaded={allHoldingsLoaded}
                            isLoading={isFetchingChunks && !allHoldingsLoaded}
                            isListView={isListView}
                            onToggleView={() => setIsListView((prev) => !prev)}
                        />
                    </div>
                )}
            </div>
            {isFiltersShown && filtersLayout({ minimizeFilter: zoomToWorld })}
            {activeCountryCode && !isSingleCountry && (
                <Details
                    activeCountryCode={activeCountryCode}
                    getCountryName={getCountryName}
                    content={content}
                    isSingleCountry={isSingleCountry}
                    countryList={countryList}
                    onClick={handleShowWorldMap}
                    pois={pois}
                />
            )}
            {isHoldingsShown && !activeCluster && (
                <DetailsHolding
                    showHoldingDetails={activeHolding !== null}
                    countryHoldingStats={computedCountryHoldingStats}
                    isStatsLoading={!allHoldingsLoaded}
                    holding={
                        pois.features.find(
                            (feature) =>
                                feature.properties.sourceId === activeHolding
                        )?.properties
                    }
                    onClick={handleCloseHoldingDetails}
                    overviewAccordion={overviewAccordion}
                    bookmarkedPages={bookmarkedPages}
                    activeCountryName={activeCountryName}
                />
            )}
            {isHoldingsShown && activeCluster && (
                <DetailsHoldingCarousel
                    holdings={activeCluster}
                    countryHoldingStats={computedCountryHoldingStats}
                    isStatsLoading={!allHoldingsLoaded}
                    overviewAccordion={overviewAccordion}
                    onClick={handleCloseHoldingDetails}
                />
            )}
        </div>
    );
};

const Map = (props) => {
    return (
        <MapProvider>
            <MapComponent {...props} />
        </MapProvider>
    );
};

// Compute aggregations from POIs for a given country
const computeCountryAggregations = (pois, countryCode) => {
    const upperCode = countryCode.toUpperCase();
    const countryPois = pois.features.filter(
        (f) =>
            f.properties.countryCode?.toUpperCase() === upperCode &&
            f.properties.category === 'Holding'
    );

    const byCompany = {};
    let totalRevenue = 0;
    let totalEmployees = 0;

    countryPois.forEach((f) => {
        const name = f.properties.name;
        const rev = f.properties.revenue || 0;
        const emp = f.properties.employeeCount || 0;

        totalRevenue += rev;
        totalEmployees += emp;

        if (!byCompany[name]) byCompany[name] = { count: 0, revenue: 0 };
        byCompany[name].count += 1;
        byCompany[name].revenue += rev;
    });

    const topCompanies = Object.entries(byCompany)
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, 3)
        .map(([name, data]) => ({
            description: name,
            value: data.count,
            revenue: data.revenue,
        }));

    return {
        topCompanies,
        data: [
            { value: totalRevenue, title: 'Total revenue', unit: 'USD' },
            {
                value: totalEmployees,
                title: 'Total employees',
                unit: 'employees',
            },
            { value: countryPois.length, title: 'Total sites', unit: 'sites' },
        ],
    };
};

export const Details = ({
    activeCountryCode,
    getCountryName,
    content,
    isSingleCountry = false,
    countryList,
    onClick,
    pois = { features: [] },
}) => {
    const country = countryList.countries.find(
        (c) => c.code.toLowerCase() === activeCountryCode
    );

    // Compute aggregations on-the-fly if not provided by SSR
    const computedAggs = computeCountryAggregations(pois, activeCountryCode);
    const topCompanies = country?.topCompanies || computedAggs.topCompanies;
    const countryData = country?.data || computedAggs.data;

    return (
        <div className={s.Details}>
            <div className={s.DetailsWrapper}>
                <Grid columns={1} modifier="WithGap">
                    <div className={s.DetailsHeader}>
                        <HeadLine
                            title={`Companies in ${getCountryName(activeCountryCode)}`}
                            size="Small">
                            <Flag
                                isSquare
                                width="20px"
                                countryCode={activeCountryCode}
                            />
                        </HeadLine>
                        <div className={s.DetailsHeaderButton}>
                            <Button
                                className={s.CloseButton}
                                modifier="Secondary"
                                title={t('map.close')}
                                onClick={onClick}
                            />
                        </div>
                    </div>
                    {topCompanies && topCompanies.length > 0 && (
                        <Box title="Top companies">
                            <ImageTextList items={topCompanies} isColumn />
                        </Box>
                    )}

                    {countryData &&
                        countryData.map((item, index) => {
                            let displayValue = item.value;
                            let displayUnit = item.unit;

                            if (item.unit.toLowerCase() === 'usd') {
                                const formatted = formatNumberWithSuffix(
                                    item.value
                                );
                                displayValue = formatted.value;
                                displayUnit = formatted.suffix;
                            } else {
                                displayValue = parseInt(item.value, 10);
                            }

                            return (
                                <Box key={index}>
                                    <SingleDataValueItem
                                        title={item.title}
                                        value={displayValue}
                                        unit={displayUnit}
                                        description=""
                                    />
                                </Box>
                            );
                        })}
                </Grid>
            </div>
            {!isSingleCountry && (
                <div className={s.ContentButton}>
                    <Button
                        modifier="Primary"
                        title="Go to country page"
                        icon="Arrow"
                        size="Large"
                        url={country?.url}
                        isFullWidth
                    />
                </div>
            )}
        </div>
    );
};

export const DetailsHolding = ({
    showHoldingDetails = false,
    countryHoldingStats = {},
    isStatsLoading = false,
    holding = {},
    overviewAccordion,
    onClick,
    activeCountryName,
}) => {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const bookmarkUrl = useMemo(() => {
        const params = new URLSearchParams(searchParams?.toString() || '');
        if (holding?.sourceId) {
            params.set('id', holding.sourceId);
        }
        const qs = params.toString();
        return qs ? `${pathname}?${qs}` : pathname;
    }, [pathname, searchParams, holding?.sourceId]);

    const [status, setStatus] = useState('idle');

    const depAndRolesItems = [
        {
            tabTitle: t('map.boardOfDirectors'),
            content: holding?.boardOfDirectors,
        },
        {
            tabTitle: t('map.seniorManagement'),
            content: holding?.seniorManagement,
        },
        {
            tabTitle: t('map.markertingAndAdvertising'),
            content: holding?.marketingAndAdvertising,
        },
        {
            tabTitle: t('map.publicAndGovernmentAffairs'),
            content: holding?.publicAndGovernmentAffairs,
        },
    ].filter(
        (item) =>
            item.content &&
            Array.isArray(item.content) &&
            item.content.length > 0
    );

    const [activeItem, setActiveItem] = useState(0);
    const formattedHoldingRevenue =
        holding.revenue !== null && holding.revenue > 0
            ? formatNumberWithSuffix(holding.revenue)
            : null;

    const formattedTotalRevenue = formatNumberWithSuffix(
        countryHoldingStats?.byClient?.reduce(
            (sum, client) => sum + (client.revenue || 0),
            0
        )
    );
    const showAddressMissing = ['city', 'country'].includes(
        holding.coordinateLevel
    );
    const tagTextKey =
        holding.coordinateLevel === 'country'
            ? 'map.tagTextCountry'
            : 'map.tagText';

    const renderTabContent = (activeItem) => {
        const item = depAndRolesItems[activeItem];

        if (!item || !item.content) return null;

        return (
            <div className={s.TabContent}>
                {item.content.map((contentItem, index) => (
                    <div key={index} className={s.TabContentItem}>
                        <span className={s.TabContentItemLabel}>
                            {contentItem.title}
                        </span>
                        <h2 className={s.TabContentItemTitle}>
                            {contentItem.value}
                        </h2>
                        <span className={s.TabContentItemDescription}>
                            {contentItem.subtitle}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className={s.DetailsHoldings}>
            <div className={s.DetailsWrapper}>
                {!showHoldingDetails && (
                    <Grid columns={1} modifier="WithGap">
                        <HeadLine
                            title={t('map.holdingsOverview')}
                            size="Small"
                        />
                        {!!overviewAccordion?.html && (
                            <div className={s.Accordion}>
                                <AccordionItem
                                    open={true}
                                    title={'Summary'}
                                    size="Medium"
                                    content={overviewAccordion?.html}
                                />
                            </div>
                        )}
                        {isStatsLoading && !countryHoldingStats.totalSites ? (
                            <>
                                <StatsSkeleton />
                                <StatsSkeleton />
                                <StatsSkeleton />
                            </>
                        ) : (
                            <>
                                <TwoColumnDataValue
                                    asItem
                                    title={t('map.totalSites')}
                                    description=""
                                    value={countryHoldingStats.totalSites}
                                    unit={t('map.sites')}>
                                    <ImageTextList
                                        items={[
                                            {
                                                icon: 'Apartment',
                                                description: t('map.numMain', {
                                                    count:
                                                        countryHoldingStats
                                                            .sitesByType
                                                            ?.Main || 0,
                                                }),
                                            },
                                            {
                                                icon: 'Homework',
                                                description: t(
                                                    'map.numSubsidiaries',
                                                    {
                                                        count:
                                                            countryHoldingStats
                                                                .sitesByType
                                                                ?.Subsidiary ||
                                                            0,
                                                    }
                                                ),
                                            },
                                            {
                                                icon: 'Support',
                                                description: t(
                                                    'map.numBranches',
                                                    {
                                                        count:
                                                            countryHoldingStats
                                                                .sitesByType
                                                                ?.Branch || 0,
                                                    }
                                                ),
                                            },
                                            {
                                                icon: 'Share',
                                                description: t(
                                                    'map.numDistributors',
                                                    {
                                                        count:
                                                            countryHoldingStats
                                                                .sitesByType
                                                                ?.Distributor ||
                                                            0,
                                                    }
                                                ),
                                            },
                                        ]}
                                        hideIfZero
                                        size="Small"
                                        isColumn
                                        isSquare
                                    />
                                </TwoColumnDataValue>

                                <TwoColumnDataValue
                                    asItem
                                    title={t('map.totalEmployees')}
                                    description=""
                                    value={countryHoldingStats.byClient?.reduce(
                                        (sum, client) =>
                                            sum + (client.employees || 0),
                                        0
                                    )}
                                    unit={t('map.employees')}>
                                    {countryHoldingStats?.byClient?.length >
                                        1 && (
                                        <ImageTextList
                                            items={countryHoldingStats.byClient
                                                .sort(
                                                    (a, b) =>
                                                        (b.employees || 0) -
                                                        (a.employees || 0)
                                                )
                                                .slice(0, 3)
                                                .map((item) => {
                                                    return {
                                                        textNoIcon: item.name,
                                                        description: t(
                                                            'map.employeesCount',
                                                            {
                                                                count: formatValue(
                                                                    item.employees
                                                                ),
                                                            }
                                                        ),
                                                    };
                                                })}
                                            hideIfZero
                                            size="Small"
                                            isColumn
                                            isSquare
                                        />
                                    )}
                                </TwoColumnDataValue>
                                <TwoColumnDataValue
                                    asItem
                                    title={t('map.totalRevenue')}
                                    description=""
                                    value={formattedTotalRevenue.value}
                                    unit={`${formattedTotalRevenue.suffix} ${t('map.usd')}`}>
                                    {countryHoldingStats?.byClient?.length >
                                        1 && (
                                        <ImageTextList
                                            items={countryHoldingStats.byClient
                                                .sort(
                                                    (a, b) =>
                                                        (b.revenue || 0) -
                                                        (a.revenue || 0)
                                                )
                                                .slice(0, 3)
                                                .map((item) => {
                                                    return {
                                                        textNoIcon: item.name,
                                                        description: t(
                                                            'map.revenueCount',
                                                            {
                                                                count: formatNumberWithSuffixAsString(
                                                                    parseInt(
                                                                        item.revenue ||
                                                                            0,
                                                                        10
                                                                    )
                                                                ),
                                                            }
                                                        ),
                                                    };
                                                })}
                                            hideIfZero
                                            size="Small"
                                            isColumn
                                            isSquare
                                        />
                                    )}
                                </TwoColumnDataValue>
                            </>
                        )}
                    </Grid>
                )}
                {showHoldingDetails && (
                    <Grid columns={1} modifier="WithGap">
                        {showAddressMissing && (
                            <div className={s.DetailsHoldingTagWrapper}>
                                <span className={s.DetailsHoldingTagText}>
                                    {t(tagTextKey)}
                                </span>
                            </div>
                        )}

                        <div className={s.DetailsHoldingsHeader}>
                            <HeadLine
                                title={
                                    showHoldingDetails
                                        ? holding.name
                                        : t('map.holdingsOverview')
                                }
                                size="Small">
                                <div className={s.DetailsIconWrapper}>
                                    <Icon
                                        name={iconType(holding.category)}
                                        className={s.DetailsIcon}
                                    />
                                </div>
                            </HeadLine>
                            <div className={s.DetailsHeaderButtons}>
                                <Bookmark
                                    url={bookmarkUrl}
                                    isSmall
                                    modifiers={['Map']}
                                    alternativeTitle={holding?.name}
                                    categories={[
                                        'Country page',
                                        'Companies',
                                        activeCountryName,
                                    ]}
                                />
                                <Button
                                    className={s.CloseButton}
                                    modifier="Secondary"
                                    title={t('map.close')}
                                    onClick={onClick}
                                />
                            </div>
                        </div>

                        {holding.globalUltimateOwner && (
                            <Box title={t('map.guo')}>
                                <h2 className={s.GuoTitle}>
                                    {holding.globalUltimateOwner}
                                </h2>
                            </Box>
                        )}

                        {(holding.fullAddress || holding.address) && (
                            <Box title={t('map.address')}>
                                <p className={s.Address}>
                                    {holding.fullAddress || holding.address}
                                </p>
                                <div className={s.AddressIconWrapper}>
                                    <div className={s.AddressIcon}>
                                        <Icon
                                            name={iconType(holding.category)}
                                        />
                                    </div>
                                    <span className={s.AddressLabel}>
                                        {holding.category}
                                    </span>
                                </div>
                            </Box>
                        )}

                        {holding.revenue !== null && holding.revenue > 0 && (
                            <Box>
                                <SingleDataValueItem
                                    title={t('map.totalRevenue')}
                                    value={formattedHoldingRevenue.value}
                                    unit={`${formattedHoldingRevenue.suffix} USD`}
                                    description={
                                        holding.lastAccountsYear
                                            ? `(${holding.lastAccountsYear})`
                                            : ''
                                    }
                                />
                            </Box>
                        )}

                        {holding.employeeCount !== null &&
                            holding.employeeCount > 0 && (
                                <Box>
                                    <SingleDataValueItem
                                        title={t('map.totalEmployees')}
                                        value={holding.employeeCount}
                                        unit="employees"
                                        description={`(${holding.lastAccountsYear})`}
                                    />
                                </Box>
                            )}

                        {depAndRolesItems.length > 0 && (
                            <Box title={t('map.departmentAndRoles')}>
                                <TabArea
                                    items={depAndRolesItems.map(
                                        (item, index) => {
                                            return {
                                                title: item.tabTitle,
                                                buttonId: index,
                                            };
                                        }
                                    )}
                                    isLink={false}
                                    activeItem={activeItem}
                                    setActiveItem={setActiveItem}
                                />
                                {renderTabContent(activeItem)}
                            </Box>
                        )}

                        {/* {holding.headOfOffice?.title && (
                            <Box>
                                <CardImage
                                    value={holding.headOfOffice.title}
                                    subtitle={holding.headOfOffice.position}
                                    title={t('map.headOfOffice')}
                                    image={holding.headOfOffice.image}
                                />
                            </Box>
                        )} */}
                    </Grid>
                )}
            </div>
        </div>
    );
};

const StatsSkeleton = () => (
    <div className={s.StatsSkeletonCard} aria-hidden="true">
        <div className={s.StatsSkeletonTitle} />
        <div className={s.StatsSkeletonValue} />
        <div className={s.StatsSkeletonChipRow}>
            <div className={s.StatsSkeletonChip} />
            <div className={s.StatsSkeletonChip} />
        </div>
    </div>
);

const DetailsHoldingCarousel = ({
    holdings,
    countryHoldingStats,
    isStatsLoading = false,
    overviewAccordion,
    onClick,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        setCurrentIndex(0);
    }, [holdings]);

    const handleNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % holdings.length);
    };

    const handlePrevious = () => {
        setCurrentIndex(
            (prevIndex) => (prevIndex - 1 + holdings.length) % holdings.length
        );
    };

    return (
        <div className={s.DetailsHoldingCarousel}>
            <DetailsHolding
                showHoldingDetails={true}
                countryHoldingStats={countryHoldingStats}
                isStatsLoading={isStatsLoading}
                holding={holdings[currentIndex]}
                overviewAccordion={overviewAccordion}
                onClick={onClick}
            />
            <div className={s.DetailsHoldingCarouselButtons}>
                <button
                    className={s.DetailsHoldingCarouselButton}
                    onClick={handlePrevious}>
                    {t('map.previous')}
                </button>
                <span className={s.DetailsHoldingCarouselCounter}>
                    {t('map.locationCount', {
                        current: currentIndex + 1,
                        total: holdings.length,
                    })}
                </span>
                <button
                    className={s.DetailsHoldingCarouselButton}
                    onClick={handleNext}>
                    {t('map.next')}
                </button>
            </div>
        </div>
    );
};

export default Map;
